import { useMemo } from "react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import { noticesV2TargetWithFilters } from "components/notices_v2/noticesV2TargetWithFilters";
import { FIND_EXPIRING_CONTRACTS_RELEVANCY_COLUMN_FIELDS } from "components/notices_v2/NoticeTableV2";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import {
  StandardViewName,
  useStandardViewTarget,
} from "lib/hooks/api/notices/views/useStandardViewTarget";
import { useTeam } from "lib/hooks/api/teams/useTeam";
import { EventData, EventNames, useTracking } from "lib/tracking";

const DEFAULT_HELP_URL = "https://help.stotles.com/faqs#how-to-build-pipeline-with-leads";

type LeadPipelineEmptyStateProps = {
  eventData: EventData;
  description: string;
  actionButtonTitle: string;
  viewName: StandardViewName;
  heading?: string;
  helpUrl?: string;
  learnMoreText?: string;
};

function LeadPipelineEmptyState({
  eventData,
  description,
  actionButtonTitle,
  viewName,
  heading = "Your Lead pipeline",
  helpUrl = DEFAULT_HELP_URL,
  learnMoreText = "Learn more",
}: LeadPipelineEmptyStateProps) {
  const { logEvent } = useTracking();
  const isRelevancyNewUsers = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const { data: team, isLoading: isTeamLoading } = useTeam({ enabled: isRelevancyNewUsers });
  const { target: standardViewTarget, isLoading: isStandardViewLoading } = useStandardViewTarget(
    viewName,
    { enabled: !isRelevancyNewUsers },
  );

  const relevancyNoticesV2Target = useMemo(() => {
    const topicFromProfile = team?.metadata?.defaultSearchTopic?.trim() ?? "";
    return noticesV2TargetWithFilters(
      {
        topicSearch: topicFromProfile,
        stage: ["AWARDED_CONTRACT"],
        expiryDate: {
          relativeFrom: "PT0S",
          hideNulls: true,
        },
      },
      FIND_EXPIRING_CONTRACTS_RELEVANCY_COLUMN_FIELDS,
    );
  }, [team?.metadata?.defaultSearchTopic]);

  const target = isRelevancyNewUsers ? relevancyNoticesV2Target : standardViewTarget;
  const isLoading = isRelevancyNewUsers ? isTeamLoading : isStandardViewLoading;

  return (
    <EmptyState
      type="startingState"
      heading={heading}
      description={<Description>{description}</Description>}
      width="100%"
      size="large"
      action={
        <Flex gap={8} flexWrap="wrap" justifyContent="center">
          <Button as="a" {...target} loading={isLoading}>
            {actionButtonTitle}
          </Button>
          <Button
            as="a"
            href={helpUrl}
            buttonType="secondary"
            onClick={() =>
              logEvent(EventNames.clickedHelpCentre, {
                ...eventData,
                "Destination url": helpUrl,
              })
            }
          >
            {learnMoreText}
          </Button>
        </Flex>
      }
    />
  );
}

const Description = styled(Flex)({
  maxWidth: 400,
});

export default LeadPipelineEmptyState;
